import * as React from 'react';
import { EntityForm, useEntityForm } from '@components/integrated/entity-form';
import type { EntityFormConfig, FieldConfig } from '@components/integrated/entity-form';
import { VStack } from '@components/base/layout';
import { Text } from '@components/base/display';
import { TextField, Select } from '@components/base/inputs';
import { Button } from '@components/base/actions/Button/Button';
import { Tabs } from '@components/base/navigation';
import { ShowcasePreviewSection } from '../../components/shared/ShowcasePreviewSection';
import { ShowcaseDemoSection } from '../../components/shared/ShowcaseDemoSection';
import type { ShowcaseDefinition, ShowcaseProps } from '../types';

// ---------------------------------------------------------------------------
// Helper: Code block for displaying configuration
// ---------------------------------------------------------------------------

function ConfigCodeBlock({ config }: Readonly<{ config: EntityFormConfig }>) {
  return (
    <pre className="bg-[#1E293B] text-[#E2E8F0] text-xs leading-relaxed p-4 rounded-lg overflow-x-auto">
      <code>{JSON.stringify(config, null, 2)}</code>
    </pre>
  );
}

// ---------------------------------------------------------------------------
// Shared form configs for showcase demos
// ---------------------------------------------------------------------------

/**
 * Basic create form config with a few fields and validation.
 */
const createUserConfig: EntityFormConfig = {
  id: 'create-user',
  endpoint: { erpEndpoint: 'users.create', method: 'POST' },
  fields: {
    firstName: {
      type: 'text',
      label: 'First Name',
      placeholder: 'Enter first name',
      validation: { required: true },
      order: 1,
    },
    lastName: {
      type: 'text',
      label: 'Last Name',
      placeholder: 'Enter last name',
      validation: { required: true },
      order: 2,
    },
    email: {
      type: 'email',
      label: 'Email Address',
      placeholder: 'user@example.com',
      validation: { required: true },
      order: 3,
    },
    role: {
      type: 'select',
      label: 'Role',
      options: {
        items: [
          { value: 'admin', label: 'Administrator' },
          { value: 'editor', label: 'Editor' },
          { value: 'viewer', label: 'Viewer' },
          { value: 'manager', label: 'Manager' },
        ],
        placeholder: 'Select a role',
      },
      validation: { required: true },
      order: 4,
    },
  },
  layout: { columns: 2, gap: 'md' },
  actions: {
    submit: { label: 'Create User', variant: 'primary' },
    cancel: { label: 'Cancel', variant: 'ghost' },
    reset: { visible: true, label: 'Reset', variant: 'secondary' },
  },
  i18n: {
    title: 'Create User',
    description: 'Fill in the details below to create a new user account.',
  },
};

/**
 * Edit form config with pre-filled initial values.
 */
const editUserConfig: EntityFormConfig = {
  id: 'edit-user',
  endpoint: { erpEndpoint: 'users.update', method: 'PUT' },
  fields: {
    firstName: {
      type: 'text',
      label: 'First Name',
      placeholder: 'Enter first name',
      validation: { required: true },
      order: 1,
    },
    lastName: {
      type: 'text',
      label: 'Last Name',
      placeholder: 'Enter last name',
      validation: { required: true },
      order: 2,
    },
    email: {
      type: 'email',
      label: 'Email Address',
      placeholder: 'user@example.com',
      validation: { required: true },
      order: 3,
    },
    role: {
      type: 'select',
      label: 'Role',
      options: {
        items: [
          { value: 'admin', label: 'Administrator' },
          { value: 'editor', label: 'Editor' },
          { value: 'viewer', label: 'Viewer' },
          { value: 'manager', label: 'Manager' },
        ],
        placeholder: 'Select a role',
      },
      order: 4,
    },
  },
  layout: { columns: 2, gap: 'md' },
  actions: {
    submit: { label: 'Save Changes', variant: 'primary' },
    cancel: { label: 'Discard', variant: 'ghost' },
  },
  i18n: {
    title: 'Edit User',
    description: 'Update the user account details.',
  },
};

/**
 * Form config using sections to group fields.
 */
const sectionsConfig: EntityFormConfig = {
  id: 'user-profile-sections',
  endpoint: { erpEndpoint: 'users.profile', method: 'POST' },
  fields: {
    firstName: {
      type: 'text',
      label: 'First Name',
      placeholder: 'John',
      validation: { required: true },
      section: 'personal',
      order: 1,
    },
    lastName: {
      type: 'text',
      label: 'Last Name',
      placeholder: 'Doe',
      validation: { required: true },
      section: 'personal',
      order: 2,
    },
    email: {
      type: 'email',
      label: 'Email',
      placeholder: 'john@example.com',
      validation: { required: true },
      section: 'contact',
      order: 1,
    },
    phone: {
      type: 'tel',
      label: 'Phone',
      placeholder: '+1 (555) 000-0000',
      section: 'contact',
      order: 2,
    },
    street: {
      type: 'text',
      label: 'Street Address',
      placeholder: '123 Main St',
      section: 'address',
      order: 1,
    },
    city: {
      type: 'text',
      label: 'City',
      placeholder: 'Springfield',
      section: 'address',
      order: 2,
    },
    state: {
      type: 'select',
      label: 'State',
      options: {
        items: [
          { value: 'CA', label: 'California' },
          { value: 'NY', label: 'New York' },
          { value: 'TX', label: 'Texas' },
          { value: 'FL', label: 'Florida' },
          { value: 'IL', label: 'Illinois' },
        ],
        placeholder: 'Select state',
      },
      section: 'address',
      order: 3,
    },
    zip: {
      type: 'text',
      label: 'ZIP Code',
      placeholder: '00000',
      section: 'address',
      order: 4,
    },
  },
  sections: [
    { id: 'personal', title: 'Personal Information', description: 'Basic identity details', order: 1 },
    { id: 'contact', title: 'Contact Details', description: 'How to reach this person', order: 2 },
    { id: 'address', title: 'Address', description: 'Mailing address', order: 3, collapsible: true },
  ],
  layout: { columns: 2, gap: 'md' },
  actions: {
    submit: { label: 'Save Profile' },
  },
  i18n: {
    title: 'User Profile',
    description: 'Complete the profile information below.',
  },
};

/**
 * Form config with conditional field visibility.
 * The "other role" text field only appears when role is "other".
 */
const conditionalConfig: EntityFormConfig = {
  id: 'conditional-demo',
  endpoint: { erpEndpoint: 'demo.conditional', method: 'POST' },
  fields: {
    name: {
      type: 'text',
      label: 'Full Name',
      placeholder: 'Jane Smith',
      validation: { required: true },
      order: 1,
    },
    contactMethod: {
      type: 'select',
      label: 'Preferred Contact Method',
      options: {
        items: [
          { value: 'email', label: 'Email' },
          { value: 'phone', label: 'Phone' },
          { value: 'mail', label: 'Postal Mail' },
        ],
        placeholder: 'Choose contact method',
      },
      validation: { required: true },
      order: 2,
    },
    emailAddress: {
      type: 'email',
      label: 'Email Address',
      placeholder: 'jane@example.com',
      conditions: [
        {
          when: 'contactMethod',
          operator: 'equals',
          value: 'email',
          effect: { visible: true, required: true },
        },
      ],
      visible: false,
      order: 3,
    },
    phoneNumber: {
      type: 'tel',
      label: 'Phone Number',
      placeholder: '+1 (555) 000-0000',
      conditions: [
        {
          when: 'contactMethod',
          operator: 'equals',
          value: 'phone',
          effect: { visible: true, required: true },
        },
      ],
      visible: false,
      order: 4,
    },
    mailingAddress: {
      type: 'textarea',
      label: 'Mailing Address',
      placeholder: 'Enter your full mailing address',
      options: { rows: 3, resize: 'vertical' as const },
      conditions: [
        {
          when: 'contactMethod',
          operator: 'equals',
          value: 'mail',
          effect: { visible: true, required: true },
        },
      ],
      visible: false,
      order: 5,
    },
  },
  layout: { columns: 1, gap: 'md' },
  actions: {
    submit: { label: 'Submit' },
    cancel: { label: 'Cancel' },
  },
  i18n: {
    title: 'Conditional Fields Demo',
    description: 'Select a contact method to see the relevant field appear.',
  },
};

/**
 * Form config demonstrating various validation rules.
 */
const validationConfig: EntityFormConfig = {
  id: 'validation-demo',
  endpoint: { erpEndpoint: 'demo.validation', method: 'POST' },
  fields: {
    username: {
      type: 'text',
      label: 'Username',
      placeholder: 'min 3 characters',
      helperText: 'Must be at least 3 characters long',
      validation: {
        required: true,
        min: { value: 3, message: 'Username must be at least 3 characters' },
      },
      order: 1,
    },
    email: {
      type: 'email',
      label: 'Email',
      placeholder: 'user@domain.com',
      helperText: 'Must be a valid email address',
      validation: { required: true },
      order: 2,
    },
    password: {
      type: 'password',
      label: 'Password',
      placeholder: 'Minimum 8 characters',
      helperText: 'At least 8 characters with one uppercase letter',
      validation: {
        required: true,
        min: { value: 8, message: 'Password must be at least 8 characters' },
        pattern: {
          value: '.*[A-Z].*',
          message: 'Password must contain at least one uppercase letter',
        },
      },
      order: 3,
    },
    age: {
      type: 'number',
      label: 'Age',
      placeholder: 'Between 18 and 120',
      helperText: 'Must be between 18 and 120',
      validation: {
        required: true,
        min: { value: 18, message: 'Must be at least 18' },
        max: { value: 120, message: 'Must be at most 120' },
      },
      order: 4,
    },
    bio: {
      type: 'textarea',
      label: 'Bio',
      placeholder: 'Tell us about yourself (max 200 chars)',
      options: { rows: 3, maxLength: 200, resize: 'vertical' as const },
      validation: {
        max: { value: 200, message: 'Bio must be 200 characters or fewer' },
      },
      order: 5,
    },
  },
  layout: { columns: 2, gap: 'md' },
  actions: {
    submit: { label: 'Register' },
    reset: { visible: true, label: 'Clear' },
  },
  i18n: {
    title: 'Validation Demo',
    description: 'Try submitting with invalid data to see validation in action.',
  },
};

// ---------------------------------------------------------------------------
// Headless Mode Sub-component
// ---------------------------------------------------------------------------

const headlessConfig: EntityFormConfig = {
  id: 'headless-demo',
  endpoint: { erpEndpoint: 'demo.headless', method: 'POST' },
  fields: {
    search: {
      type: 'text',
      label: 'Search',
      placeholder: 'Type to search...',
      order: 1,
    },
    category: {
      type: 'select',
      label: 'Category',
      options: {
        items: [
          { value: 'all', label: 'All' },
          { value: 'users', label: 'Users' },
          { value: 'posts', label: 'Posts' },
        ],
      },
      defaultValue: 'all',
      order: 2,
    },
  },
  layout: { columns: 1 },
};

/**
 * Headless usage example -- uses the useEntityForm hook directly
 * without the compound component wrapper.
 */
// Pre-resolved field configs for HeadlessFormDemo – avoids optional chaining
// on known-defined fields so that every runtime branch is exercisable in tests.
const headlessSearchField = headlessConfig.fields.search as FieldConfig;
const headlessCategoryField = headlessConfig.fields.category as FieldConfig;
const headlessCategoryItems = (headlessCategoryField.options as { items: { value: string; label: string }[] }).items;

function HeadlessFormDemo() {
  const form = useEntityForm({
    config: headlessConfig,
    onSuccess: () => {
      // eslint-disable-next-line no-console
      console.log('[HeadlessFormDemo] submit success', form.values);
    },
  });

  const searchProps = form.getFieldProps('search');
  const categoryProps = form.getFieldProps('category');

  return (
    <VStack gap={4} className="max-w-lg">
      <Text size="sm" color="muted">
        This form is built using the <code>useEntityForm</code> hook directly,
        without the <code>EntityForm</code> compound component. Full control
        over rendering while retaining all form state management.
      </Text>

      <div className="flex gap-3 items-end">
        <div className="flex-1">
          <TextField
            label={headlessSearchField.label}
            placeholder={headlessSearchField.placeholder}
            value={(searchProps.value as string) ?? ''}
            onChange={(e) => { searchProps.onChange(e.target.value); }}
            onBlur={searchProps.onBlur}
            error={searchProps.error}
            errorMessage={searchProps.errorMessage}
          />
        </div>

        <div className="w-40">
          <Select
            label="Category"
            options={headlessCategoryItems}
            value={categoryProps.value as string | undefined}
            onChange={categoryProps.onChange}
            onBlur={categoryProps.onBlur}
            disabled={categoryProps.disabled}
          />
        </div>

        <Button
          type="button"
          variant="primary"
          size="md"
          onClick={() => { void form.submit(); }}
          loading={form.isSubmitting}
        >
          Search
        </Button>
      </div>

      <div className="flex gap-4 text-xs">
        <Text size="xs" color="muted">
          Dirty: {form.isDirty ? 'Yes' : 'No'}
        </Text>
        <Text size="xs" color="muted">
          Values: {JSON.stringify(form.values)}
        </Text>
      </div>
    </VStack>
  );
}

// ---------------------------------------------------------------------------
// Remote Configuration Usage Code
// ---------------------------------------------------------------------------

const remoteConfigUsageCode = `// Remote configuration mode
// The form config is fetched from the API endpoint:
// GET /api/{module}/config → { data: { config: { "create-user": {...} } } }

<EntityForm
  remoteConfigId="create-user"
  onSuccess={handleSuccess}
  onCancel={handleCancel}
>
  <EntityForm.Header />
  <EntityForm.Alert />
  <EntityForm.Fields />
  <EntityForm.Actions />
</EntityForm>

// Optional props:
// - tenant="custom-tenant"    → Override tenant from context
// - module="custom-module"    → Override module from context
// - loadingFallback={<MyLoader />} → Custom loading UI
// - errorMessage="Custom error"    → Custom error message

// Prerequisites:
// Wrap your app with IntegratedComponentProvider:
<IntegratedComponentProvider
  apiClient={axiosInstance}
  tenant="tenant-id"
  module="my-module"
>
  <App />
</IntegratedComponentProvider>`;

// ---------------------------------------------------------------------------
// Main Showcase Component
// ---------------------------------------------------------------------------

function EntityFormShowcaseComponent({
  playgroundProps,
  isDarkMode: _isDarkMode,
}: Readonly<ShowcaseProps>) {
  const previewColumns = (playgroundProps.columns as number) ?? 2;
  const previewDensity =
    (playgroundProps.density as 'compact' | 'default' | 'comfortable') ??
    'default';
  const previewShowHeader = playgroundProps.showHeader !== false;
  const previewShowActions = playgroundProps.showActions !== false;

  /**
   * Build a memoised preview config so the form doesn't
   * re-mount on every playground prop change.
   */
  const previewConfig: EntityFormConfig = React.useMemo(
    () => ({
      ...createUserConfig,
      layout: {
        ...createUserConfig.layout,
        columns: previewColumns as 1 | 2 | 3 | 4,
        density: previewDensity,
      },
    }),
    [previewColumns, previewDensity],
  );

  return (
    <VStack gap={6}>
      {/* ---- Interactive Preview ---- */}
      <ShowcasePreviewSection componentName="entity form">
        <div className="w-full max-w-2xl">
          <EntityForm
            config={previewConfig}
            onSuccess={() => {
              // eslint-disable-next-line no-console
              console.log('[EntityFormShowcase] submit success');
            }}
            onCancel={() => {
              // eslint-disable-next-line no-console
              console.log('[EntityFormShowcase] cancel');
            }}
          >
            {previewShowHeader && <EntityForm.Header />}
            <EntityForm.Alert />
            <EntityForm.Fields />
            {previewShowActions && <EntityForm.Actions />}
          </EntityForm>
        </div>
      </ShowcasePreviewSection>

      {/* ---- Basic Create Form ---- */}
      <ShowcaseDemoSection
        title="Basic Create Form"
        description="A simple form with text, email, and select fields using the compound component pattern"
       
      >
        <Tabs defaultValue="preview" variant="contained">
          <Tabs.List>
            <Tabs.Tab value="preview">Preview</Tabs.Tab>
            <Tabs.Tab value="config">Configuration</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="preview">
            <div className="w-full max-w-2xl">
              <EntityForm
                config={createUserConfig}
                onSuccess={() => {
                  // eslint-disable-next-line no-console
                  console.log('[CreateForm] submitted');
                }}
                onCancel={() => {
                  // eslint-disable-next-line no-console
                  console.log('[CreateForm] cancelled');
                }}
              >
                <EntityForm.Header />
                <EntityForm.Alert />
                <EntityForm.Fields />
                <EntityForm.Actions />
              </EntityForm>
            </div>
          </Tabs.Panel>
          <Tabs.Panel value="config">
            <ConfigCodeBlock config={createUserConfig} />
          </Tabs.Panel>
        </Tabs>
      </ShowcaseDemoSection>

      {/* ---- Edit Form with Prefilled Data ---- */}
      <ShowcaseDemoSection
        title="Edit Form with Prefilled Data"
        description="Form pre-populated with initial values, simulating an edit workflow"
       
      >
        <Tabs defaultValue="preview" variant="contained">
          <Tabs.List>
            <Tabs.Tab value="preview">Preview</Tabs.Tab>
            <Tabs.Tab value="config">Configuration</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="preview">
            <div className="w-full max-w-2xl">
              <EntityForm
                config={editUserConfig}
                mode="edit"
                initialValues={{
                  firstName: 'Jane',
                  lastName: 'Doe',
                  email: 'jane.doe@company.com',
                  role: 'editor',
                }}
                onSuccess={() => {
                  // eslint-disable-next-line no-console
                  console.log('[EditForm] saved');
                }}
                onCancel={() => {
                  // eslint-disable-next-line no-console
                  console.log('[EditForm] discarded');
                }}
              >
                <EntityForm.Header />
                <EntityForm.Alert />
                <EntityForm.Fields />
                <EntityForm.Actions />
              </EntityForm>
            </div>
          </Tabs.Panel>
          <Tabs.Panel value="config">
            <ConfigCodeBlock config={editUserConfig} />
          </Tabs.Panel>
        </Tabs>
      </ShowcaseDemoSection>

      {/* ---- Form with Sections ---- */}
      <ShowcaseDemoSection
        title="Form with Sections"
        description="Fields grouped into collapsible Card sections for better organisation"
       
      >
        <Tabs defaultValue="preview" variant="contained">
          <Tabs.List>
            <Tabs.Tab value="preview">Preview</Tabs.Tab>
            <Tabs.Tab value="config">Configuration</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="preview">
            <div className="w-full max-w-2xl">
              <EntityForm
                config={sectionsConfig}
                onSuccess={() => {
                  // eslint-disable-next-line no-console
                  console.log('[SectionsForm] submitted');
                }}
              >
                <EntityForm.Header />
                <EntityForm.Alert />
                <EntityForm.Fields />
                <EntityForm.Actions />
              </EntityForm>
            </div>
          </Tabs.Panel>
          <Tabs.Panel value="config">
            <ConfigCodeBlock config={sectionsConfig} />
          </Tabs.Panel>
        </Tabs>
      </ShowcaseDemoSection>

      {/* ---- Conditional Fields ---- */}
      <ShowcaseDemoSection
        title="Conditional Fields"
        description="Select a contact method to reveal the corresponding field. Fields are hidden/shown based on another field's value."
       
      >
        <Tabs defaultValue="preview" variant="contained">
          <Tabs.List>
            <Tabs.Tab value="preview">Preview</Tabs.Tab>
            <Tabs.Tab value="config">Configuration</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="preview">
            <div className="w-full max-w-lg">
              <EntityForm
                config={conditionalConfig}
                onSuccess={() => {
                  // eslint-disable-next-line no-console
                  console.log('[ConditionalForm] submitted');
                }}
              >
                <EntityForm.Header />
                <EntityForm.Alert />
                <EntityForm.Fields />
                <EntityForm.Actions />
              </EntityForm>
            </div>
          </Tabs.Panel>
          <Tabs.Panel value="config">
            <ConfigCodeBlock config={conditionalConfig} />
          </Tabs.Panel>
        </Tabs>
      </ShowcaseDemoSection>

      {/* ---- Form with Validation ---- */}
      <ShowcaseDemoSection
        title="Form with Validation"
        description="Demonstrates required fields, minimum length, pattern matching, and range validation. Submit the empty form to see all errors."
       
      >
        <Tabs defaultValue="preview" variant="contained">
          <Tabs.List>
            <Tabs.Tab value="preview">Preview</Tabs.Tab>
            <Tabs.Tab value="config">Configuration</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="preview">
            <div className="w-full max-w-2xl">
              <EntityForm
                config={validationConfig}
                onSuccess={() => {
                  // eslint-disable-next-line no-console
                  console.log('[ValidationForm] submitted');
                }}
              >
                <EntityForm.Header />
                <EntityForm.Alert />
                <EntityForm.Fields />
                <EntityForm.Actions />
              </EntityForm>
            </div>
          </Tabs.Panel>
          <Tabs.Panel value="config">
            <ConfigCodeBlock config={validationConfig} />
          </Tabs.Panel>
        </Tabs>
      </ShowcaseDemoSection>

      {/* ---- Headless Mode ---- */}
      <ShowcaseDemoSection
        title="Headless Mode"
        description="Using the useEntityForm hook directly for full rendering control without the compound component"
       
      >
        <Tabs defaultValue="preview" variant="contained">
          <Tabs.List>
            <Tabs.Tab value="preview">Preview</Tabs.Tab>
            <Tabs.Tab value="config">Configuration</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="preview">
            <HeadlessFormDemo />
          </Tabs.Panel>
          <Tabs.Panel value="config">
            <ConfigCodeBlock config={headlessConfig} />
          </Tabs.Panel>
        </Tabs>
      </ShowcaseDemoSection>

      {/* ---- Remote Configuration ---- */}
      <ShowcaseDemoSection
        title="Remote Configuration"
        description="This form loads its configuration from the remote API endpoint (GET /api/component-explorer/config) using the remoteConfigId prop. The IntegratedComponentProvider in the component-explorer routes supplies the API client, tenant and module."
       
      >
        <Tabs defaultValue="preview" variant="contained">
          <Tabs.List>
            <Tabs.Tab value="preview">Preview</Tabs.Tab>
            <Tabs.Tab value="config">Configuration</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="preview">
            <div className="w-full max-w-2xl">
              <EntityForm
                remoteConfigId="create-user"
                onSuccess={() => {
                  // eslint-disable-next-line no-console
                  console.log('[RemoteConfigForm] submitted');
                }}
                onCancel={() => {
                  // eslint-disable-next-line no-console
                  console.log('[RemoteConfigForm] cancelled');
                }}
              >
                <EntityForm.Header />
                <EntityForm.Alert />
                <EntityForm.Fields />
                <EntityForm.Actions />
              </EntityForm>
            </div>
          </Tabs.Panel>
          <Tabs.Panel value="config">
            <pre className="bg-[#1E293B] text-[#E2E8F0] text-xs leading-relaxed p-4 rounded-lg overflow-x-auto">
              <code>{remoteConfigUsageCode}</code>
            </pre>
          </Tabs.Panel>
        </Tabs>
      </ShowcaseDemoSection>
    </VStack>
  );
}

// ---------------------------------------------------------------------------
// Showcase Definition
// ---------------------------------------------------------------------------

export const EntityFormShowcase: ShowcaseDefinition = {
  id: 'entity-form',
  name: 'EntityForm',
  category: 'forms',
  description:
    'Configuration-driven form component with validation, conditional fields, sections, and headless mode',
  propsDefinition: [
    {
      name: 'columns',
      type: 'select',
      options: ['1', '2', '3', '4'],
      defaultValue: '2',
      description: 'Number of grid columns for the form layout',
    },
    {
      name: 'density',
      type: 'select',
      options: ['compact', 'default', 'comfortable'],
      defaultValue: 'default',
      description: 'Spacing density affecting field sizes and gaps',
    },
    {
      name: 'showHeader',
      type: 'boolean',
      defaultValue: true,
      description: 'Show the form header with title and description',
    },
    {
      name: 'showActions',
      type: 'boolean',
      defaultValue: true,
      description: 'Show the action buttons (submit, cancel, reset)',
    },
  ],
  accessibility: {
    ariaAttributes: [
      'aria-label on form element (from config title)',
      'aria-busy during form submission',
      'aria-required on required fields',
      'aria-invalid on fields with validation errors',
      'aria-describedby linking fields to helper/error text',
      'role="alert" on form-level error/success alerts',
    ],
    keyboardNav: [
      'Tab: Navigate between form fields',
      'Enter: Submit form (when focused on submit button)',
      'Escape: Close any open dropdowns',
    ],
    notes: [
      'Form uses noValidate to handle validation programmatically',
      'Error messages are announced to screen readers via live regions',
      'Sections use Card or fieldset for semantic grouping',
      'Each field gets a unique id for label association',
    ],
  },
  component: EntityFormShowcaseComponent,
};
